// B4-ITEM-TEMPLATES (universal task plan §a "Task templates", §c wave 2): a board's item
// templates. Work OS's task templates ("save as", "create from"; payload JSON) kept as templates
// per board: a person saves a task as one (or writes one), `POST tasks {template_id}` makes a task
// from it, and a DE proposes them (MCP `create_item_template`, a `board_config` approval under an
// Ask). Base plan (`feature: core`). Handlers: services/api/src/routes/work/item-templates.ts.
import { z } from "zod";
import { text, Uuid } from "../primitives";
import { ItemTemplate, ItemTemplatePayload } from "../work-rows";
import { CompanyItemParams } from "./common";
import { defineRoute } from "./route";
import { BoardItemParams } from "./work";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;
const boardSub = (path: string) => `${base}/boards/{id}/${path}`;
/** An agent's write held to its policy (`board_config`): Never refused, an Ask answers 409. */
const AGENT_POLICY = ["policy_denied", "conflict"] as const;

/** What a template saved from a task takes from it (all on unless turned off). */
export const ItemTemplateInclude = z
  .strictObject({
    description: z.boolean().optional(),
    checklists: z.boolean().optional().describe("Every checklist's items, as one checklist"),
    fields: z.boolean().optional(),
    assignees: z.boolean().optional().describe("Default: off (a template is often for others)"),
    tags: z.boolean().optional(),
    dates: z
      .boolean()
      .optional()
      .describe("Due and start as days after the task was made (when it has them)"),
  })
  .meta({ id: "ItemTemplateInclude" });

export const ItemTemplateCreate = z
  .strictObject({
    name: text(120),
    description: text(500).nullable().optional(),
    payload: ItemTemplatePayload.optional().describe("The task shape, written outright"),
    from_task_id: Uuid.optional().describe("Save this task of the board as the template"),
    include: ItemTemplateInclude.optional().describe("With `from_task_id`: what it takes"),
  })
  .refine((b) => (b.payload === undefined) !== (b.from_task_id === undefined), {
    message: "give exactly one of payload and from_task_id",
  })
  .meta({ id: "ItemTemplateCreate" });

export const ItemTemplatePatch = z
  .strictObject({
    name: text(120).optional(),
    description: text(500).nullable().optional(),
    payload: ItemTemplatePayload.optional(),
    position: z.int().min(0).optional(),
  })
  .meta({ id: "ItemTemplatePatch" });

export const ItemTemplateList = z
  .object({ data: z.array(ItemTemplate) })
  .meta({ id: "ItemTemplateList" });

export const itemTemplateRoutes = {
  "itemTemplates.list": defineRoute({
    ...core,
    id: "itemTemplates.list",
    method: "get",
    path: boardSub("item-templates"),
    tag: "boards",
    summary: "A board's item templates (live ones, in order)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: ItemTemplateList },
    errors: [],
    emits: [],
  }),
  "itemTemplates.create": defineRoute({
    ...core,
    id: "itemTemplates.create",
    method: "post",
    path: boardSub("item-templates"),
    tag: "boards",
    summary:
      "Save an item template on a board: a task of the board saved as one (`from_task_id`), or a task shape written outright (`payload`). A DE's write is held to its policy (`board_config`)",
    access: "member",
    params: CompanyItemParams,
    body: ItemTemplateCreate,
    idempotency: "optional",
    response: { status: 201, schema: ItemTemplate },
    errors: [...AGENT_POLICY],
    emits: ["item_template.created"],
  }),
  "itemTemplates.update": defineRoute({
    ...core,
    id: "itemTemplates.update",
    method: "patch",
    path: boardSub("item-templates/{sub_id}"),
    tag: "boards",
    summary: "Rename an item template or change its task shape",
    access: "member",
    params: BoardItemParams,
    body: ItemTemplatePatch,
    response: { status: 200, schema: ItemTemplate },
    errors: [...AGENT_POLICY],
    emits: ["item_template.updated"],
  }),
  "itemTemplates.archive": defineRoute({
    ...core,
    id: "itemTemplates.archive",
    method: "delete",
    path: boardSub("item-templates/{sub_id}"),
    tag: "boards",
    summary: "Archive an item template (tasks made from it keep their link)",
    access: "member",
    params: BoardItemParams,
    response: { status: 204 },
    errors: [...AGENT_POLICY],
    emits: ["item_template.archived"],
  }),
};
